import { getApi } from 'api';
import { AxiosResponse } from 'axios';
import { UPDropdownItem } from 'components/dropdown';
import { EmployeeExistsResponse } from 'constants/EmployeeExistsResponse';
import { fromEmployeeRequestToEmployeeModel } from 'mappers/employee-mapper';
import dayjs from 'utils/dayjs';
import BlobResponseType from 'types/blob/BlobResponseType';
import { IDocument } from 'types/documents';
import { IAddEditEmployee, IEmployeeGridFilters, IEmployeeTotals } from 'types/employee';
import ICompanyInvitationLink from 'types/employee/ICompanyInvitationLink';
import IEmployeeGridDetails from 'types/employee/IEmployeeGridDetails';
import IEmployeeInvitationResponse from 'types/employee/IEmployeeInvitationResponse';
import IEmployeeOnboardingDetails from 'types/employee/IEmployeeOnboardingDetails';
import IEmployeeWithWorkerclass from 'types/employee/IEmployeeWithWorkerclass';
import IOnboardingLogin from 'types/employee/IOnboardingLogin';
import IOnboardingLoginResponse from 'types/employee/IOnboardingLoginResponse';
import { PaginatedList } from 'types/PaginatedList';

export const saveEmployeeRequest = async (
  employee: any,
  companyId: number | undefined,
  invitationToken?: string,
): Promise<number> => {
  const formData = new FormData();
  Object.keys(employee).map(key => key !== 'address' && key !== 'dependants' && formData.append(key, employee[key]));
  Object.keys(employee.address).map(key => formData.append(`address[${key}]`, employee.address[key]));
  if (employee.dependants !== null) {
    Object.keys(employee.dependants).map(key => formData.append(`dependants[${key}]`, employee.dependants[key]));
  }

  if (employee.stayAddress !== null) {
    Object.keys(employee.stayAddress).map(key => formData.append(`stayAddress[${key}]`, employee.stayAddress[key]));
  }

  if (employee.companyLocationIds) {
    formData.delete('companyLocationIds');
    Object.keys(employee.companyLocationIds).map(k =>
      formData.append(`companyLocationIds[${k}]`, employee.companyLocationIds[k]),
    );
  }

  const response: AxiosResponse<number> = await getApi().post(`Employee`, formData, {
    headers: { 'Content-Type': `multipart/form-data`, companyId, invitationToken },
  });
  return response.data;
};

export const getAllEmployees = async (props: IEmployeeGridFilters): Promise<PaginatedList<IEmployeeGridDetails>> => {
  const {
    status,
    companyId,
    pageSize,
    pageIndex,
    workType,
    age,
    terminated,
    invited,
    eligible,
    sortOrder,
    sortCriteria,
    name,
    locationId,
  } = props;
  const response: AxiosResponse<PaginatedList<IEmployeeGridDetails>> = await getApi().post(
    `Employee/GetAll`,
    {
      status,
      pageSize,
      pageIndex,
      workType,
      age,
      terminated,
      invited,
      eligible,
      sortOrder,
      sortCriteria,
      name,
      locationId,
    },
    {
      headers: {
        companyId,
      },
    },
  );
  return response.data;
};

export const getEmployeeByIdRequest = async (employeeId: number, companyId: number): Promise<IAddEditEmployee> => {
  const response: AxiosResponse<IAddEditEmployee> = await getApi().get(`Employee/${employeeId}`, {
    headers: { companyId },
  });
  return fromEmployeeRequestToEmployeeModel(response.data);
};

export const getEmployeeSelfOnboardingRequest = async (
  companyToken: string,
  inviteToken: string,
): Promise<IEmployeeOnboardingDetails> => {
  const response: AxiosResponse<IEmployeeOnboardingDetails> = await getApi().get(
    `Employee/EmployeeSelfOnboarding/${companyToken}/${inviteToken}`,
    {
      headers: { companyId: companyToken },
    },
  );
  return response.data;
};

export const getEmployeeSelfOnboardingWithGeneralLinkRequest = async (
  inviteToken: string,
): Promise<IEmployeeOnboardingDetails> => {
  const response: AxiosResponse<IEmployeeOnboardingDetails> = await getApi().get(
    `CompanyOnboarding/selfOnboarding/${inviteToken}`,
  );
  return response.data;
};

/** Branch dropdown for the personal-invitation onboarding screen. Scoped by the invitation tokens
 *  rather than by a company id, so it stays usable before the invitee holds an access token. */
export const getOnboardingLocationsAsSelectItemsRequest = async (
  companyToken: string,
  inviteToken: string,
): Promise<UPDropdownItem[]> => {
  const response: AxiosResponse<UPDropdownItem[]> = await getApi().get(
    `Employee/EmployeeSelfOnboarding/${companyToken}/${inviteToken}/Locations`,
    {
      headers: { companyId: companyToken },
    },
  );
  return response.data;
};

/** Same, for the general onboarding link, where the company is resolved from the link itself. */
export const getGeneralLinkOnboardingLocationsAsSelectItemsRequest = async (
  inviteToken: string,
): Promise<UPDropdownItem[]> => {
  const response: AxiosResponse<UPDropdownItem[]> = await getApi().get(
    `CompanyOnboarding/selfOnboarding/${inviteToken}/Locations`,
  );
  return response.data;
};

export const updateEmployeeRequest = async (employee: any, companyId: number): Promise<number> => {
  const formData = new FormData();
  Object.keys(employee).map(key => key !== 'address' && key !== 'dependants' && formData.append(key, employee[key]));
  Object.keys(employee.address).map(key => formData.append(`address[${key}]`, employee.address[key]));

  if (employee.dependants !== null) {
    Object.keys(employee.dependants).map(key => formData.append(`dependants[${key}]`, employee.dependants[key]));
  }

  if (employee.stayAddress !== null) {
    Object.keys(employee.stayAddress).map(key => formData.append(`stayAddress[${key}]`, employee.stayAddress[key]));
  }

  if (employee.companyLocationIds) {
    formData.delete('companyLocationIds');
    Object.keys(employee.companyLocationIds).map(k =>
      formData.append(`companyLocationIds[${k}]`, employee.companyLocationIds[k]),
    );
  }

  const response: AxiosResponse<number> = await getApi().put(`Employee`, formData, {
    headers: { companyId, 'Content-Type': `multipart/form-data` },
  });

  return response.data;
};

export const getEmployeesWithWorkerclassRequest = async (companyId: number): Promise<IEmployeeWithWorkerclass[]> => {
  const response: AxiosResponse<IEmployeeWithWorkerclass[]> = await getApi().get(
    `Employee/GetEmployeesWithWorkerclass`,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const getEmployeesDropdownItemsRequest = async (companyId: number): Promise<UPDropdownItem[]> => {
  const response: AxiosResponse<UPDropdownItem[]> = await getApi().get('Employee/GetEmployeesDropdownItems', {
    headers: { companyId },
  });
  return response.data;
};

export const inviteEmployeeRequest = async (props: {
  employee: any;
  companyId: string;
}): Promise<IEmployeeInvitationResponse> => {
  const { companyId, employee } = props;
  const response: AxiosResponse<IEmployeeInvitationResponse> = await getApi().post(
    `Employee/InviteEmployee`,
    employee,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const resendInvitationRequest = async (props: {
  emailAddress: string;
  companyId: string;
}): Promise<IEmployeeInvitationResponse> => {
  const { companyId, emailAddress } = props;
  const response: AxiosResponse<IEmployeeInvitationResponse> = await getApi().post(
    `Employee/ResendInvitation`,
    { emailAddress },
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const getEmployeeCountRequest = async (props: {
  companyId: number;
  weekNumber: number;
}): Promise<IEmployeeTotals> => {
  const { companyId, weekNumber } = props;
  const response: AxiosResponse<IEmployeeTotals> = await getApi().get(`Employee/GetEmployeeCount/${weekNumber}`, {
    headers: {
      companyId,
    },
  });
  return response.data;
};

export const downloadIdImageRequest = async (props: {
  companyId: number;
  employeeId: number;
  imageName: string;
}): Promise<BlobResponseType> => {
  const { employeeId, imageName, companyId } = props;
  const response: AxiosResponse<number> = await getApi().get(`File/GetContent?eid=${employeeId}&img=${imageName}`, {
    headers: { companyId },
    responseType: 'blob',
  });

  const result: BlobResponseType = {
    content: response.data,
    contentType: response.headers['content-type'],
  };

  return result;
};

export const downloadEmployeesRequest = async (props: IEmployeeGridFilters): Promise<any> => {
  const {
    companyId,
    pageSize,
    pageIndex,
    workType,
    age,
    terminated,
    invited,
    eligible,
    sortOrder,
    sortCriteria,
    name,
    locationId,
  } = props;
  const response: AxiosResponse<number> = await getApi().post(
    `DataExport/ExportEmployeeData`,
    { pageSize, pageIndex, workType, age, terminated, eligible, invited, sortOrder, sortCriteria, name, locationId },
    {
      headers: {
        companyId,
      },
      responseType: 'blob',
    },
  );
  return response.data;
};

export const getCompanyEmployeeInvitationLinksRequest = async (
  companyId: number,
): Promise<ICompanyInvitationLink[]> => {
  const response: AxiosResponse<ICompanyInvitationLink[]> = await getApi().get(`CompanyOnboarding/Get/${companyId}`);
  return response.data;
};

export const deletetCompanyEmployeeInvitationLinksRequest = async (id: number): Promise<void> => {
  await getApi().delete(`CompanyOnboarding/${id}`);
};

export const addCompanyInvitationRequest = async (data: ICompanyInvitationLink): Promise<number> => {
  const response: AxiosResponse<number> = await getApi().post(`CompanyOnboarding/New`, data, {
    headers: { companyId: data.companyId },
  });
  return response.data;
};

export const employeeInvitationTokenValidityRequest = async (
  invitationToken: string,
  companyToken: string,
): Promise<boolean> => {
  const response: AxiosResponse<boolean> = await getApi().get(
    `Employee/EmployeeInvitationTokenValidity/${invitationToken}/${companyToken}`,
  );
  return response.data;
};

export const getActiveEmployeeDropdownItemsRequest = async (companyId: number): Promise<UPDropdownItem[]> => {
  const r: AxiosResponse<UPDropdownItem[]> = await getApi().get(`Employee/GetActiveEmployeesDropdown`, {
    headers: { companyId },
  });
  return r.data;
};

export const checkEmployeeEmailAndInszExistRequest = async (
  companyId: string,
  email: string,
  insz: string,
): Promise<EmployeeExistsResponse> => {
  const encodedEmail = encodeURIComponent(email);
  const encodedInsz = encodeURIComponent(insz);

  const response: AxiosResponse<EmployeeExistsResponse> = await getApi().get(
    `Employee/EmployeeExistsByEmailAndInsz?email=${encodedEmail}&insz=${encodedInsz}`,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const checkTerminatedEmployeeExistsRequest = async (
  companyId: string,
  email: string,
  insz: string,
): Promise<boolean> => {
  const encodedEmail = encodeURIComponent(email);
  const encodedInsz = encodeURIComponent(insz);

  const response: AxiosResponse<boolean> = await getApi().get(
    `Employee/TerminatedEmployeeExistsByEmailAndInsz?email=${encodedEmail}&insz=${encodedInsz}`,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const getExistingEmployeeTokenRequest = async (
  data: IOnboardingLogin,
): Promise<IOnboardingLoginResponse | null> => {
  try {
    const response: AxiosResponse<IOnboardingLoginResponse | null> = await getApi().post(`Auth/OnboardingLogin`, data);
    return response.data;
  } catch (error: any) {
    return null;
  }
};

export const getEmployeeByIdWithTokenRequest = async (
  loginResponse: IOnboardingLoginResponse,
): Promise<IAddEditEmployee> => {
  const response: AxiosResponse<IAddEditEmployee> = await getApi().get(`Employee/${loginResponse.employeeId}`, {
    headers: { companyId: loginResponse.companyId, Authorization: `Bearer ${loginResponse.token}` },
  });
  return fromEmployeeRequestToEmployeeModel(response.data);
};

export const getDocumentsAtOnboardingRequest = async (props: {
  companyId: number;
  category: number | null;
  employeeId: number | null;
  startDate: Date | null;
  endDate: Date | null;
  token: string;
}): Promise<PaginatedList<IDocument>> => {
  const { companyId, category, startDate, endDate, employeeId, token } = props;
  const response: AxiosResponse<PaginatedList<IDocument>> = await getApi().post(
    'Documents/GetAll',
    { category, employeeId, startDate, endDate },
    { headers: { companyId, Authorization: `Bearer ${token}` } },
  );

  return response.data;
};

export const downloadImagesAtOnboardingExisting = async (props: {
  companyId: number;
  employeeId: number;
  imageName: string;
  token: string;
}): Promise<BlobResponseType> => {
  const { employeeId, imageName, companyId, token } = props;
  const response: AxiosResponse<number> = await getApi().get(`File/GetContent?eid=${employeeId}&img=${imageName}`, {
    headers: { companyId, Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });

  const result: BlobResponseType = {
    content: response.data,
    contentType: response.headers['content-type'],
  };

  return result;
};

export const canSetEmployeeInactive = async (
  employeeId: number,
  companyId: number,
  terminationDate: string,
): Promise<boolean> => {
  const date = dayjs(terminationDate).format('YYYY-MM-DD');
  const response: AxiosResponse<boolean> = await getApi().get(
    `EmployeeValidation/canSetInactive/${companyId}/${employeeId}/${date}`,
  );
  return response.data;
};
